import {
  Collection,
  Plugin,
  createDropdown,
  addListToDropdown,
} from 'ckeditor5'
import { CONFIG } from './config'
import AiAssistant from './modal'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
const highlightedRangeRef = { current: null }

const getParentTextAtCursor = editor => {
  const model = editor.model
  const selection = model.document.selection
  const position = selection.getFirstPosition()

  // Get the parent element of the cursor's position
  const parentElement = position.parent

  if (!parentElement || !parentElement.is('element')) {
    return '' // Return empty if there's no valid parent element
  }

  // Collect text from all child nodes within the parent element
  let textContent = ''
  for (const child of parentElement.getChildren()) {
    if (child.is('text')) {
      textContent += child.data
    }
  }

  model.change(writer => {
    // Create a range that covers all content in the parent element
    const range = writer.createRange(
      writer.createPositionAt(parentElement, 0), // Start position at the beginning of parent
      writer.createPositionAt(parentElement, 'end'), // End position at the end of parent
    )
    editor.editing.view.focus()
    //writer.setSelection(range);

    if (editor.model.markers.has('persistentHighlight')) {
      writer.removeMarker('persistentHighlight')
    }

    // Add a persistent marker to the range
    writer.addMarker('persistentHighlight', {
      range,
      usingOperation: false,
      affectsData: false,
    })
    highlightedRangeRef.current = range
  })

  return textContent
}

const getSelectedText = (editor, range) => {
  const selection = editor.model.document.selection
  const selectedContent = editor.model.getSelectedContent(selection)

  const selectedText = Array.from(range.getItems())
    .map(item => item.data)
    .join('')

  const model = editor.model

  model.change(writer => {
    editor.editing.view.focus()
    //writer.setSelection(range);

    if (editor.model.markers.has('persistentHighlight')) {
      writer.removeMarker('persistentHighlight')
    }

    // Add a persistent marker to the range
    writer.addMarker('persistentHighlight', {
      range,
      usingOperation: false,
      affectsData: false,
    })
    highlightedRangeRef.current = range
  })
  if (selectedContent._children._nodes.length === 1) {
    return selectedText
  }
  return editor.data.processor.toData(editor.data.toView(selectedContent))
}

const removeHighlightMarker = editor => {
  const model = editor.model

  model.change(writer => {
    // Check if the marker exists before attempting to remove it
    if (model.markers.has('persistentHighlight')) {
      writer.removeMarker('persistentHighlight')
    }
  })
}

const aiAssistantClickHandler = (dispatch, editor, prompt) => {
  const selection = editor.model.document.selection
  const range = selection.getFirstRange()

  let selectedText
  if (range.start.path.toString() != range.end.path.toString()) {
    selectedText = getSelectedText(editor, range)
  } else {
    selectedText = getParentTextAtCursor(editor)
  }

  const handleReplaceHighlightedText = response => {
    const marker = editor.model.markers.get('persistentHighlight')

    if (marker) {
      editor.model.change(writer => {
        const range = marker.getRange()

        // Remove the existing content within the range
        writer.remove(range)

        // Convert HTML to view fragment
        const viewFragment = editor.data.processor.toView(response)

        // Convert view fragment to model fragment
        const modelFragment = editor.data.toModel(viewFragment)

        // Insert HTML content at the start of the range
        editor.model.insertContent(modelFragment, range.start)

        // Remove the marker after replacing the text
        writer.removeMarker('persistentHighlight')
      })
      editor.editing.view.focus()
    } else {
      alert('Please highlight text first!')
    }
    dispatch(messageBoxHideAction())
  }

  // The handleAddTextBelow function might be unnecessary if you only want replacement
  // If you're still using it, here's how to fix it:
  const handleAddTextBelow = response => {
    const marker = editor.model.markers.get('persistentHighlight')

    if (marker) {
      editor.model.change(writer => {
        const range = marker.getRange()

        // Convert HTML to view fragment
        const viewFragment = editor.data.processor.toView(response)

        // Convert view fragment to model fragment
        const modelFragment = editor.data.toModel(viewFragment)

        // Insert HTML content at the end of the range (no new paragraph)
        editor.model.insertContent(modelFragment, range.end)

        // Remove the highlight marker
        writer.removeMarker('persistentHighlight')
      })
      editor.editing.view.focus()
    } else {
      alert('Please highlight text first!')
    }

    dispatch(messageBoxHideAction())
  }

  const onOk = item => {
    removeHighlightMarker(editor)
    return false
  }
  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        buttons: [],
        title: `<div
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            "
          >
            <img
              src="/assets/mcg-ai-new-pluses.png"
              alt=""
              style="
                width: 20px;
                height: 20px;
              "
            ></img>
            <Typography>AI Assistant</Typography>
          </div>`,
        component: (
          <AiAssistant
            onOk={onOk}
            prompt={prompt + ' ' + selectedText}
            handleAddTextBelow={handleAddTextBelow}
            handleReplaceText={handleReplaceHighlightedText}
          />
        ),
        draggable: true,
        callback: () => {
          removeHighlightMarker(editor)
        },
      }),
    ),
  )
}

class CustomDropdown extends Plugin {
  constructor(editor) {
    super(editor)
    this.dispatch = editor.config.get('imageGalleryPlugin.dispatch')
    this.dropdownView = null
  }

  init() {
    const editor = this.editor
    const t = editor.t

    editor.ui.componentFactory.add('customDropdown', locale => {
      const items = new Collection()

      CONFIG.forEach(item => {
        return items.add({
          type: 'button',
          model: {
            withText: true,
            label: item.label,
            prompt: item.prompt,
          },
        })
      })

      const svg = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.22255 18.3328C7.58487 18.3328 7.88177 18.0663 7.93209 17.6893C8.43028 14.3308 8.88821 13.8632 12.1189 13.4962C12.4913 13.4509 12.7781 13.1392 12.7781 12.7772C12.7781 12.4102 12.4963 12.1086 12.1239 12.0533C8.91337 11.6058 8.51582 11.2136 7.93209 7.86019C7.86667 7.48815 7.57983 7.22168 7.22255 7.22168C6.8552 7.22168 6.56333 7.48815 6.50294 7.86522C6.01482 11.2187 5.55689 11.6862 2.33124 12.0533C1.9488 12.1035 1.66699 12.4052 1.66699 12.7772C1.66699 13.1392 1.93873 13.4409 2.32118 13.4962C5.53676 13.9537 5.92927 14.3408 6.50294 17.6943C6.57843 18.0714 6.87029 18.3328 7.22255 18.3328Z" fill="url(#paint0_linear_15468_105007)"/>
                    <path d="M14.8609 8.61144C15.0873 8.61144 15.2729 8.4449 15.3044 8.20922C15.6157 6.11018 15.9019 5.81795 17.9211 5.58856C18.1538 5.56028 18.3331 5.36546 18.3331 5.13921C18.3331 4.90983 18.157 4.72129 17.9242 4.68673C15.9177 4.40706 15.6692 4.16196 15.3044 2.06606C15.2635 1.83353 15.0842 1.66699 14.8609 1.66699C14.6313 1.66699 14.4489 1.83353 14.4111 2.0692C14.1061 4.16511 13.8199 4.45734 11.8038 4.68673C11.5648 4.71815 11.3887 4.90669 11.3887 5.13921C11.3887 5.36546 11.5585 5.554 11.7975 5.58856C13.8073 5.87451 14.0526 6.11646 14.4111 8.21237C14.4583 8.44804 14.6407 8.61144 14.8609 8.61144Z" fill="url(#paint1_linear_15468_105007)"/>
                    <path d="M16.5974 18.3336C16.7107 18.3336 16.8034 18.2503 16.8192 18.1324C16.9749 17.0829 17.118 16.9368 18.1275 16.8221C18.2439 16.808 18.3336 16.7106 18.3336 16.5974C18.3336 16.4827 18.2455 16.3885 18.1291 16.3712C17.1258 16.2314 17.0016 16.1088 16.8192 15.0609C16.7987 14.9446 16.7091 14.8613 16.5974 14.8613C16.4826 14.8613 16.3914 14.9446 16.3726 15.0624C16.22 16.1104 16.0769 16.2565 15.0689 16.3712C14.9494 16.3869 14.8613 16.4812 14.8613 16.5974C14.8613 16.7106 14.9462 16.8048 15.0658 16.8221C16.0706 16.9651 16.1933 17.0861 16.3726 18.134C16.3962 18.2519 16.4874 18.3336 16.5974 18.3336Z" fill="url(#paint2_linear_15468_105007)"/>
                    <defs>
                    <linearGradient id="paint0_linear_15468_105007" x1="1.66699" y1="12.7772" x2="10.3776" y2="12.7772" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#50A7FF"/>
                    <stop offset="1" stop-color="#5C68FA"/>
                    </linearGradient>
                    <linearGradient id="paint1_linear_15468_105007" x1="11.3887" y1="5.13921" x2="16.8328" y2="5.13921" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#50A7FF"/>
                    <stop offset="1" stop-color="#5C68FA"/>
                    </linearGradient>
                    <linearGradient id="paint2_linear_15468_105007" x1="14.8613" y1="16.5974" x2="17.5834" y2="16.5974" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#50A7FF"/>
                    <stop offset="1" stop-color="#5C68FA"/>
                    </linearGradient>
                    </defs>
                    </svg>`
      const dropdown = createDropdown(locale)
      this.dropdownView = dropdown

      addListToDropdown(dropdown, items)

      this.listenTo(dropdown, 'execute', evt => {
        aiAssistantClickHandler(this.dispatch, editor, evt.source.prompt)
      })

      dropdown.buttonView.set({
        label: t('AI Assistant'),
        icon: svg,
        tooltip: false,
        withText: true,
        class: 'ai-asistant',
      })

      // Add selection change listener to check if we're in a drop-area-wide
      this.listenTo(editor.model.document.selection, 'change:range', () => {
        this._refreshState()
      })

      // Also check when data changes
      this.listenTo(editor.model.document, 'change:data', () => {
        this._refreshState()
      })

      // Set initial state
      this._refreshState()

      return dropdown
    })
  }

  // Add this new method to check if selection is inside drop-area-wide
  _isSelectionInDropAreaWide() {
    const editor = this.editor
    const selection = editor.model.document.selection
    const selectedElement = selection.getSelectedElement()

    // Check if selected element is a drop-area-wide
    if (selectedElement && selectedElement.name === 'dropAreaWide') {
      return true
    }

    // Check if the selection is inside a drop-area-wide
    if (selection.getFirstPosition()) {
      let parent = selection.getFirstPosition().parent

      // Walk up through the model tree
      while (parent) {
        if (parent.name === 'dropAreaWide') {
          return true
        }
        parent = parent.parent
      }
    }

    return false
  }

  // Method to refresh the button state
  _refreshState() {
    if (!this.dropdownView) {
      return
    }

    const isInDropAreaWide = this._isSelectionInDropAreaWide()

    // Disable the button if selection is in drop-area-wide
    this.dropdownView.buttonView.isEnabled = !isInDropAreaWide
  }
}

export default CustomDropdown
